import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { CloseOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    IconButton,
    LinearProgress,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';

import { DataImportRetrieveModelActionEnum } from '@swagger-http';

import { useDataImportAddImportBySiteId } from '@queries/status';
import { handleError } from '@tools/utils/debug';

import styles from '@containers/customer/smart-energy/index.module.scss';

type TriggerDataImportDialogProps = {
    siteId: string;
    configId: string;
    handleClose: () => void;
};

const TriggerDataImportDialog: React.FC<TriggerDataImportDialogProps> = ({
    siteId,
    configId,
    handleClose,
}) => {
    const { t } = useTranslation();

    const [action, setAction] = useState<DataImportRetrieveModelActionEnum>(
        DataImportRetrieveModelActionEnum.Reimport,
    );

    const [pageError, setPageError] = useState('');
    const { mutateAsync, isPending: isLoading } =
        useDataImportAddImportBySiteId(siteId);

    const translatedOptions: Partial<
        Record<DataImportRetrieveModelActionEnum, string>
    > = useMemo(
        () => ({
            [DataImportRetrieveModelActionEnum.Reimport]: t(
                'smartEnergyDevicesTab.triggerDataImport.options.reimport',
            ),
            [DataImportRetrieveModelActionEnum.ExtendHistoricalData]: t(
                'smartEnergyDevicesTab.triggerDataImport.options.extendHistoricalData',
            ),
        }),
        [t],
    );

    const handleTrigger = useCallback(async () => {
        try {
            setPageError('');

            await mutateAsync({
                action,
                configId,
            });
            handleClose();
        } catch (e) {
            const err = await handleError(e);

            setPageError(err);
        }
    }, [action, configId, handleClose, mutateAsync]);

    return (
        <Dialog
            open
            fullWidth
            onClick={(e) => e.stopPropagation()}
            onClose={handleClose}
            maxWidth="sm"
            className={styles.dialog}
            sx={
                isLoading
                    ? {
                          '.MuiPaper-root': {
                              padding: 0,
                          },
                      }
                    : {}
            }
        >
            {isLoading ? (
                <LinearProgress sx={{ m: 0 }} color="primary" />
            ) : null}
            <Box className={styles.dialogHeader}>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    className={styles.iconButton}
                    disabled={isLoading}
                >
                    <CloseOutlined />
                </IconButton>

                <DialogTitle>
                    {t('smartEnergyDevicesTab.triggerDataImport.title')}
                </DialogTitle>
            </Box>
            {!isLoading ? (
                <DialogContent className={styles.content}>
                    <DialogContentText>
                        {t('smartEnergyDevicesTab.triggerDataImport.label')}
                    </DialogContentText>
                    <FormControl
                        variant="standard"
                        sx={{ m: 1, minWidth: 120 }}
                    >
                        <Select
                            data-testid="trigger-action-select"
                            value={action}
                            onChange={(e: any) => setAction(e.target.value)}
                        >
                            {[
                                DataImportRetrieveModelActionEnum.Reimport,
                                DataImportRetrieveModelActionEnum.ExtendHistoricalData,
                            ].map((value) => (
                                <MenuItem key={value} value={value}>
                                    {translatedOptions[value]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {pageError && (
                        <Typography className={styles.error}>
                            {t(
                                'smartEnergyDevicesTab.triggerDataImport.error',
                                {
                                    errorMessage: pageError,
                                },
                            )}
                        </Typography>
                    )}
                    <Box
                        sx={{
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            sx={{ marginX: 1 }}
                            variant="contained"
                            className={styles.cancelButton}
                            onClick={handleClose}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            sx={{ marginX: 1 }}
                            variant="contained"
                            onClick={handleTrigger}
                        >
                            {t(
                                'smartEnergyDevicesTab.triggerDataImport.button',
                            )}
                        </Button>
                    </Box>
                </DialogContent>
            ) : (
                <Box
                    sx={{
                        paddingBottom: 3,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        {t(
                            'smartEnergyDevicesTab.removeInstallation.submitting',
                        )}
                    </Typography>
                </Box>
            )}
        </Dialog>
    );
};

export default TriggerDataImportDialog;
